import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { trackEvent, getTrackingMeta, trackFormStart, trackQuoteSubmit, trackAddonSelected, trackPlanSelected, SERVICE } from "@/lib/tracking";
import { ADDONS } from "@/components/sections/PricingSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROBLEM_OPTIONS = [
  "We need more enquiries",
  "We need better quality leads",
  "We need more online sales",
  "We need more bookings or consultations",
  "We need more registrations for an event, course, or programme",
  "We are spending on ads but not seeing results",
  "We need to retarget people who visited our website",
  "We need help choosing between Google, Meta, and LinkedIn Ads",
  "We need automated follow-up for leads",
  "We need tracking and reporting fixed",
  "Other",
];

const TIMING_OPTIONS = [
  "Within 5 days",
  "Within 14 days",
  "By the end of this month",
  "Next month",
  "Not sure yet",
];

const BUDGET_OPTIONS = [
  "Less than R3,000 p/m",
  "R3,000 - R5,000 p/m",
  "R5,000 - R10,000 p/m",
  "R10,000 - R25,000 p/m",
  "R25,000 - R50,000 p/m",
  "More than R50,000 p/m",
  "Not sure yet",
];

const SUPPORT_OPTIONS = [
  { value: "dwy", label: "DWY Paid Ads Enablement - from R4,850 p/m" },
  { value: "dfy", label: "DFY Paid Ads Management - from R8,890 p/m" },
  { value: "growth", label: "Growth Paid Ads System - from R13,500 p/m" },
  { value: "not_sure", label: "Not sure yet" },
];

const PLAN_TO_SUPPORT = { dwy: "dwy", dfy: "dfy", growth: "growth" };

const FIELD_BASE =
  "bg-white border border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:border-[#1a73e8] focus-visible:ring-2 focus-visible:ring-[#1a73e8]/20 rounded-lg h-12 transition-shadow";

export default function QuoteForm({ selectedPlan, setSelectedPlan, addons, setAddons }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company_name: "",
    website_url: "",
    business_email: "",
    mobile_number: "",
    problems: [],
    problem_other: "",
    go_live_timing: "",
    support_option: "",
    monthly_budget: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (selectedPlan && PLAN_TO_SUPPORT[selectedPlan]) {
      setForm((f) => ({ ...f, support_option: PLAN_TO_SUPPORT[selectedPlan] }));
    }
  }, [selectedPlan]);

  const handleField = (k, v) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("form_start");
      trackFormStart({ service: SERVICE.paid_ads, formName: "paid_ads_quote_form" });
    }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggleProblem = (opt) => {
    handleField(
      "problems",
      form.problems.includes(opt)
        ? form.problems.filter((p) => p !== opt)
        : [...form.problems, opt]
    );
  };

  const toggleAddon = (id, eventKey) => {
    setAddons((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (next[id]) {
        trackEvent(eventKey, { addon: id, source: "form" });
        trackAddonSelected({ service: SERVICE.paid_ads, addonId: id });
      }
      return next;
    });
  };

  const validate = () => {
    if (!form.first_name.trim()) return "First name is required.";
    if (!form.last_name.trim()) return "Last name is required.";
    if (!form.company_name.trim()) return "Company name is required.";
    if (!form.business_email.trim() || !/.+@.+\..+/.test(form.business_email))
      return "A valid business email is required.";
    if (!form.mobile_number.trim()) return "Mobile number is required.";
    if (form.problems.length === 0) return "Please select at least one problem.";
    if (form.problems.includes("Other") && !form.problem_other.trim())
      return "Please describe the problem.";
    if (!form.go_live_timing) return "Please tell us when you want to go live.";
    if (!form.support_option) return "Please select a support option.";
    if (!form.monthly_budget) return "Please choose a monthly media budget.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    const selectedAddons = ADDONS.filter((a) => addons[a.id]).map((a) => a.label);

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      company_name: form.company_name.trim(),
      website_url: form.website_url.trim() || null,
      business_email: form.business_email.trim(),
      mobile_number: form.mobile_number.trim(),
      problems: form.problems,
      problem_other: form.problems.includes("Other") ? form.problem_other.trim() : null,
      go_live_timing: form.go_live_timing,
      support_option: form.support_option,
      monthly_budget: form.monthly_budget,
      addons: selectedAddons,
      message: form.message.trim() || null,
      tracking: getTrackingMeta(),
    };

    try {
      setSubmitting(true);
      await axios.post(`${API}/quote`, payload);
      trackEvent("form_submit", {
        plan: form.support_option,
        addons: selectedAddons,
        budget: form.monthly_budget,
      });
      trackQuoteSubmit({
        service: SERVICE.paid_ads,
        planId: form.support_option,
        addonIds: ADDONS.filter((a) => addons[a.id]).map((a) => a.id),
        budget: form.monthly_budget,
        goLive: form.go_live_timing,
      });
      setSubmitted(true);
      toast.success("Quote request received");
    } catch (err) {
      console.error(err);
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="quote-form"
        data-testid="quote-form-success"
        className="relative py-24 sm:py-32 bg-white"
      >
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <div className="h-16 w-16 rounded-full bg-[#34a853]/10 border border-[#34a853]/30 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8 text-[#34a853]" />
          </div>
          <h2 className="font-display mt-6 text-3xl sm:text-5xl font-black tracking-tight text-zinc-900">
            Thank you. Your request has been received.
          </h2>
          <p className="mt-4 text-zinc-600 leading-relaxed text-lg">
            Rekisa will review your details and respond with the next step.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="quote-form"
      data-testid="quote-form-section"
      className="relative py-24 sm:py-32 bg-gradient-to-b from-zinc-50 to-white"
    >
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <div className="text-center mb-10">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#1a73e8] font-mono mb-3 font-bold">Quote Form</div>
          <h2 className="font-display text-4xl sm:text-6xl font-black tracking-[-0.03em] leading-[1.02] text-zinc-900">
            Request a Paid Ads Quote
          </h2>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="quote-form"
          className="rounded-3xl border border-zinc-200 bg-white p-6 sm:p-10 space-y-7 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.15)]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="First Name" required>
              <Input
                data-testid="field-first-name"
                className={FIELD_BASE}
                value={form.first_name}
                onChange={(e) => handleField("first_name", e.target.value)}
                autoComplete="given-name"
              />
            </Field>
            <Field label="Last Name" required>
              <Input
                data-testid="field-last-name"
                className={FIELD_BASE}
                value={form.last_name}
                onChange={(e) => handleField("last_name", e.target.value)}
                autoComplete="family-name"
              />
            </Field>
          </div>

          <Field label="Company Name" required>
            <Input
              data-testid="field-company"
              className={FIELD_BASE}
              value={form.company_name}
              onChange={(e) => handleField("company_name", e.target.value)}
              autoComplete="organization"
            />
          </Field>

          <Field label="Website URL">
            <Input
              data-testid="field-website"
              className={FIELD_BASE}
              placeholder="https://yourwebsite.co.za"
              type="url"
              value={form.website_url}
              onChange={(e) => handleField("website_url", e.target.value)}
              autoComplete="url"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Business Email" required>
              <Input
                data-testid="field-email"
                className={FIELD_BASE}
                type="email"
                value={form.business_email}
                onChange={(e) => handleField("business_email", e.target.value)}
                autoComplete="email"
              />
            </Field>
            <Field label="Mobile Number" required>
              <Input
                data-testid="field-mobile"
                className={FIELD_BASE}
                type="tel"
                inputMode="tel"
                value={form.mobile_number}
                onChange={(e) => handleField("mobile_number", e.target.value)}
                autoComplete="tel"
              />
            </Field>
          </div>

          <div>
            <FieldHeader label="What lead generation problem do you need to solve?" required />
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {PROBLEM_OPTIONS.map((opt) => {
                const checked = form.problems.includes(opt);
                return (
                  <label
                    key={opt}
                    htmlFor={`problem-${opt}`}
                    data-testid={`field-problem-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
                      checked
                        ? "border-[#1a73e8] bg-[#e8f0fe]"
                        : "border-zinc-200 bg-white hover:border-zinc-300"
                    }`}
                  >
                    <Checkbox
                      id={`problem-${opt}`}
                      checked={checked}
                      onCheckedChange={() => toggleProblem(opt)}
                      className="mt-0.5 border-zinc-300 data-[state=checked]:bg-[#1a73e8] data-[state=checked]:border-[#1a73e8]"
                    />
                    <span className="text-sm text-zinc-800 leading-snug">{opt}</span>
                  </label>
                );
              })}
            </div>
            {form.problems.includes("Other") && (
              <div className="mt-4">
                <Field label="Please describe the problem" required>
                  <Textarea
                    data-testid="field-problem-other"
                    className={`${FIELD_BASE} min-h-[100px] py-3 h-auto`}
                    value={form.problem_other}
                    onChange={(e) => handleField("problem_other", e.target.value)}
                  />
                </Field>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="When do you want to go live?" required>
              <Select
                value={form.go_live_timing}
                onValueChange={(v) => handleField("go_live_timing", v)}
              >
                <SelectTrigger data-testid="field-timing" className={`${FIELD_BASE} text-left`}>
                  <SelectValue placeholder="Choose a timing" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200 text-zinc-900">
                  {TIMING_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-zinc-100">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Which support option are you interested in?" required>
              <Select
                value={form.support_option}
                onValueChange={(v) => {
                  handleField("support_option", v);
                  if (PLAN_TO_SUPPORT[v]) setSelectedPlan(v);
                  trackEvent("pricing_plan_selected", { plan: v, source: "form" });
                  trackPlanSelected({ service: SERVICE.paid_ads, planId: v });
                }}
              >
                <SelectTrigger data-testid="field-support" className={`${FIELD_BASE} text-left`}>
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200 text-zinc-900">
                  {SUPPORT_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value} className="focus:bg-zinc-100">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="What monthly media budget can you start with?" required>
            <Select
              value={form.monthly_budget}
              onValueChange={(v) => handleField("monthly_budget", v)}
            >
              <SelectTrigger data-testid="field-budget" className={`${FIELD_BASE} text-left`}>
                <SelectValue placeholder="Choose a budget range" />
              </SelectTrigger>
              <SelectContent className="bg-white border-zinc-200 text-zinc-900">
                {BUDGET_OPTIONS.map((b) => (
                  <SelectItem key={b} value={b} className="focus:bg-zinc-100">
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div>
            <FieldHeader label="Add-ons" />
            <p className="text-xs text-zinc-500 mt-1">Select what you'd like included in your quote.</p>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {ADDONS.map((addon) => {
                const checked = !!addons[addon.id];
                return (
                  <label
                    key={addon.id}
                    htmlFor={`form-addon-${addon.id}`}
                    data-testid={`field-addon-${addon.id}`}
                    className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
                      checked
                        ? "border-[#1a73e8] bg-[#e8f0fe]"
                        : "border-zinc-200 bg-white hover:border-zinc-300"
                    }`}
                  >
                    <Checkbox
                      id={`form-addon-${addon.id}`}
                      checked={checked}
                      onCheckedChange={() => toggleAddon(addon.id, addon.eventKey)}
                      className="mt-0.5 border-zinc-300 data-[state=checked]:bg-[#1a73e8] data-[state=checked]:border-[#1a73e8]"
                    />
                    <span className="text-sm text-zinc-800 leading-snug">
                      <span className="block font-medium">{addon.label.replace(/^Add\s+/, "")}</span>
                      <span className="block text-xs text-zinc-500 mt-0.5">{addon.price}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <Field label="Message">
            <Textarea
              data-testid="field-message"
              className={`${FIELD_BASE} min-h-[140px] py-3 h-auto`}
              placeholder="Tell us what you want to promote and any important context."
              value={form.message}
              onChange={(e) => handleField("message", e.target.value)}
            />
          </Field>

          <div className="pt-2">
            <Button
              type="submit"
              data-testid="quote-form-submit"
              disabled={submitting}
              className="w-full bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full h-14 text-base font-semibold shadow-[0_8px_24px_rgba(26,115,232,0.35)] hover:shadow-[0_12px_32px_rgba(26,115,232,0.45)] transition-all disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...
                </>
              ) : (
                <>
                  Request a Quote <ArrowUpRight className="ml-1 h-5 w-5" />
                </>
              )}
            </Button>
            <p className="mt-4 text-center text-xs text-zinc-500">
              We respect your privacy. Your details are only used to respond to your quote request.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}

function FieldHeader({ label, required }) {
  return (
    <Label className="text-sm font-semibold text-zinc-800">
      {label}
      {required && <span className="text-[#ea4335] ml-1">*</span>}
    </Label>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <FieldHeader label={label} required={required} />
      {children}
    </div>
  );
}
